import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { Domain } from "~/api/Api";
import { SiteUpdateAttributes } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import {
  AiOptStatus,
  AiVisibilityOptimizationsPanel,
} from "~/components/siteSettings/AiVisibilityOptimizationsPanel";
import type {
  AiVisibilityOptimizationsKey,
  AiVisibilityOptimizationsValues,
} from "~/components/siteSettings/AiVisibilityOptimizationsPanel";
import {
  AI_VISIBILITY_STATUS_UI,
  getAiOptDisabled,
  getAiOptStatus,
} from "~/components/siteSettings/aiVisibilityStatus";
import { domainKeys } from "~/components/siteSpeed/queryKeys";
import Modal from "~/components/ui/modal/Modal";
import Spinner from "~/components/ui/spinner/Spinner";
import { canMember } from "~/helpers/authorizations";
import { useSite } from "~/hooks/useSite";

import styles from "./SiteModal.module.scss";

type Props = {
  onClose: () => void;
  domain: Domain;
  siteId: number | null;
};

type Draft = {
  site_speed_requested: boolean;
  prerendering: boolean;
  apex_redirect: boolean;
  inject_widget: boolean;
  inject_markdown: "hidden" | "visible" | "shortcut" | null;
  disable_lazy_automation: boolean;
  disable_defer_automation: boolean;
  disable_preload_automation: boolean;
};

const DRAFT_KEYS = [
  "prerendering",
  "apex_redirect",
  "inject_widget",
  "inject_markdown",
  "disable_lazy_automation",
  "disable_defer_automation",
  "disable_preload_automation",
] as const;

type DraftKey = (typeof DRAFT_KEYS)[number];

type DraftToggleKey = Exclude<
  AiVisibilityOptimizationsKey,
  "site_speed_requested"
>;

export const SiteModal = ({ siteId, onClose, domain }: Props) => {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);

  const { data, isLoading } = useSite(siteId);

  const site = data?.site;
  const authorizations = data?.authorizations;

  const canUpdateSite = canMember(authorizations, "update");
  const canEditCrawlSettings = canMember(authorizations, "recrawl");

  const status: AiOptStatus = useMemo(
    () => getAiOptStatus(site?.site_speed_status, site?.site_speed_requested),
    [site],
  );

  useEffect(() => {
    if (!site) return;

    setDraft({
      site_speed_requested: !!site.site_speed_requested,
      prerendering: !!site.prerendering,
      apex_redirect: !!site.apex_redirect,
      inject_widget: !!site.inject_widget,
      inject_markdown: site.inject_markdown ?? null,
      disable_lazy_automation: !!site.disable_lazy_automation,
      disable_defer_automation: !!site.disable_defer_automation,
      disable_preload_automation: !!site.disable_preload_automation,
    });
  }, [site]);

  const { mutateAsync: updateSite, isPending: isUpdating } = useMutation({
    mutationFn: async (payload: SiteUpdateAttributes) => {
      return apiClient.sites.sitesPartialUpdate(Number(siteId), payload);
    },
    onSuccess: () => {
      enqueueSnackbar("Site settings updated successfully!", {
        variant: "success",
      });
      qc.invalidateQueries({ queryKey: ["site"] });
      qc.invalidateQueries({ queryKey: ["sites"] });
      qc.invalidateQueries({ queryKey: domainKeys.sites(domain.id) });
    },
    // @ts-expect-error BE types
    onError: ({ error }) => {
      enqueueSnackbar(
        error?.error?.message || "Site settings are not updated!",
        {
          variant: "error",
          persist: true,
        },
      );
    },
  });

  const { mutateAsync: requestSiteSpeed, isPending: isApplying } = useMutation({
    mutationFn: async () =>
      apiClient.sites.siteSpeedApplyCreate(Number(siteId)),
    onSuccess: () => {
      enqueueSnackbar("Site speed status updated successfully!", {
        variant: "success",
      });
      qc.invalidateQueries({ queryKey: ["site"] });
      qc.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: () => {
      enqueueSnackbar("Site speed status is not updated!", {
        variant: "error",
        persist: true,
      });
    },
  });

  const handleClose = useCallback(() => {
    setDraft(null);
    onClose();
  }, [onClose]);

  const isPending = isUpdating || isApplying;

  const computeChangedValues = useCallback(() => {
    if (!site || !draft) {
      return {
        changed: {} as Partial<Pick<SiteUpdateAttributes, DraftKey>>,
        requestTurnedOn: false,
      };
    }

    const changed: Partial<Pick<SiteUpdateAttributes, DraftKey>> = {};

    DRAFT_KEYS.forEach((k) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (draft[k] !== site[k]) (changed as any)[k] = draft[k];
    });

    return {
      changed,
      // Only an unrequested -> requested transition submits an application.
      requestTurnedOn: !site.site_speed_requested && draft.site_speed_requested,
    };
  }, [site, draft]);

  const isDirty = useMemo(() => {
    const { changed, requestTurnedOn } = computeChangedValues();

    return requestTurnedOn || Object.keys(changed).length > 0;
  }, [draft, site]);

  const onSave = async () => {
    if (!site || !draft) return;

    const { changed, requestTurnedOn } = computeChangedValues();

    if (requestTurnedOn) {
      await requestSiteSpeed();
    }

    if (Object.keys(changed).length > 0) {
      await updateSite(changed);
    }

    qc.invalidateQueries({ queryKey: domainKeys.detail(domain.id) });

    handleClose();
  };

  const panelValues = useMemo(() => {
    if (!draft) return null;

    return {
      site_speed_requested: draft.site_speed_requested,
      prerendering: draft.prerendering,
      apex_redirect: draft.apex_redirect,
      inject_widget: draft.inject_widget,
      inject_markdown: draft.inject_markdown,
      disable_lazy_automation: draft.disable_lazy_automation,
      disable_defer_automation: draft.disable_defer_automation,
      disable_preload_automation: draft.disable_preload_automation,
    };
  }, [draft]);

  const panelDisabled = useMemo(
    () => getAiOptDisabled(canUpdateSite, canEditCrawlSettings),
    [canUpdateSite, canEditCrawlSettings],
  );

  const handleToggle = <K extends AiVisibilityOptimizationsKey>(
    key: K,
    value: AiVisibilityOptimizationsValues[K],
  ) => {
    setDraft((prev) => {
      if (!prev) return prev;

      if (key === "site_speed_requested") {
        return { ...prev, site_speed_requested: Boolean(value) };
      }

      const k = key as DraftToggleKey;

      return { ...prev, [k]: value };
    });
  };

  if (!siteId || !site) return null;

  const statusUi = AI_VISIBILITY_STATUS_UI[status];

  return (
    <Modal
      size="lg"
      isOpen={!!siteId}
      closeModal={handleClose}
      modalLabel={<div>AI Visibility Engine Optimizations</div>}
    >
      <div className={styles.modalInner}>
        <div className={styles.scrollableContent}>
          {isLoading || !panelValues ? (
            <Spinner />
          ) : (
            <AiVisibilityOptimizationsPanel
              status={status}
              notification={statusUi.notification}
              integrationType={site?.delegated_domain?.integration_type}
              savedPrerendering={!!site?.prerendering}
              siteId={siteId}
              fqdn={site?.fqdn}
              alt_fqdn={site?.alt_fqdn}
              values={panelValues}
              disabled={panelDisabled}
              onToggle={handleToggle}
            />
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!isDirty || isPending}>
            {isPending && <Spinner size="1rem" />} Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};
